import React from 'react';
import { Link } from 'react-router-dom';
import { 
    AcademicCapIcon, 
    NewspaperIcon, 
    BeakerIcon, 
    InformationCircleIcon,
    EnvelopeIcon,
    MapPinIcon,
    ShieldCheckIcon
} from '@heroicons/react/24/outline';

const Footer = () => {
    const currentYear = new Date().getFullYear();
    
    const quickLinks = [
        { to: '/', label: 'Home' },
        { to: '/courses', label: 'Courses', icon: AcademicCapIcon },
        { to: '/news', label: 'News', icon: NewspaperIcon },
        { to: '/research', label: 'Research', icon: BeakerIcon },
        { to: '/info', label: 'Aero Facts', icon: InformationCircleIcon },
        { to: '/verify', label: 'Verify Certificate', icon: ShieldCheckIcon }
    ];

    const legalLinks = [
        { to: '/about', label: 'About Us' },
        { to: '/privacy', label: 'Privacy Policy' },
        { to: '/terms', label: 'Terms of Service' },
        { to: '/faq', label: 'FAQ' },
        { to: '/contact', label: 'Contact' }
    ];

    return (
        <footer className="bg-gradient-to-b from-light-card to-light-bg dark:from-dark-card dark:to-dark-bg border-t border-light-border dark:border-dark-border mt-20">
            <div className="container mx-auto px-6 py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    
                    {/* Brand Section */}
                    <div className="lg:col-span-2">
                        <Link to="/" className="inline-block mb-4">
                            <h3 className="text-2xl md:text-3xl font-bold group">
                                <span className="text-light-text dark:text-dark-text group-hover:text-light-primary dark:group-hover:text-dark-primary transition-colors">
                                    Aircore
                                </span>
                                <span className="bg-gradient-to-r from-light-primary to-blue-500 dark:from-dark-primary dark:to-cyan-400 bg-clip-text text-transparent">
                                    Dynamics
                                </span>
                            </h3>
                        </Link>
                        <p className="text-sm text-light-text/70 dark:text-dark-text/70 max-w-md mb-6 leading-relaxed">
                            Interactive educational platform making aviation, aerospace, and aerodynamics accessible to everyone. 
                            Let's shape the technologies of the future together.
                        </p>
                        
                        {/* Contact Info */}
                        <div className="space-y-2 text-sm text-light-text/70 dark:text-dark-text/70">
                            <div className="flex items-center gap-2">
                                <EnvelopeIcon className="h-4 w-4 text-light-primary dark:text-dark-primary" />
                                <a href="mailto:info@aircoredynamics.com" className="hover:text-light-primary dark:hover:text-dark-primary transition-colors">
                                    info@aircoredynamics.com
                                </a>
                            </div>
                            <div className="flex items-center gap-2">
                                <MapPinIcon className="h-4 w-4 text-light-primary dark:text-dark-primary" />
                                <span>Worldwide</span>
                            </div>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="font-bold text-light-text dark:text-dark-text mb-4 text-lg">Quick Access</h4>
                        <ul className="space-y-3">
                            {quickLinks.map(link => (
                                <li key={link.to}>
                                    <Link 
                                        to={link.to} 
                                        className="text-sm text-light-text/70 dark:text-dark-text/70 hover:text-light-primary dark:hover:text-dark-primary transition-colors flex items-center gap-2 group"
                                    >
                                        {link.icon && <link.icon className="h-4 w-4 opacity-50 group-hover:opacity-100" />}
                                        <span>{link.label}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Resources & Social */}
                    <div>
                        <h4 className="font-bold text-light-text dark:text-dark-text mb-4 text-lg">Resources</h4>
                        <ul className="space-y-3 mb-6">
                            {legalLinks.map(link => (
                                <li key={link.to}>
                                    <Link 
                                        to={link.to} 
                                        className="text-sm text-light-text/70 dark:text-dark-text/70 hover:text-light-primary dark:hover:text-dark-primary transition-colors"
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        {/* Social Media */}
                        <h4 className="font-bold text-light-text dark:text-dark-text mb-4 text-lg">Social Media</h4>
                        <div className="flex gap-3">
                            <a 
                                href="https://instagram.com/aircoredynamics" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="p-2 rounded-lg bg-light-bg dark:bg-dark-bg hover:bg-light-primary dark:hover:bg-dark-primary hover:text-white transition-all group"
                                aria-label="Instagram"
                            >
                                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.85s-.011 3.584-.069 4.85c-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07s-3.584-.012-4.85-.07c-3.252-.148-4.771-1.691-4.919-4.919-.058-1.265-.069-1.645-.069-4.85s.011-3.584.069-4.85c.149-3.225 1.664-4.771 4.919-4.919C8.416 2.175 8.796 2.163 12 2.163zm0 1.44s-3.442.01-4.693.062c-2.61.12-3.774 1.264-3.894 3.894C3.35 8.78 3.36 9.16 3.36 12s-.01 3.22.053 4.458c.12 2.63 1.284 3.774 3.894 3.894 1.25.053 4.693.062 4.693.062s3.442-.01 4.693-.062c2.61-.12 3.774-1.264 3.894-3.894.062-1.25.053-4.693.053-4.693s.01-3.442-.053-4.693c-.12-2.63-1.284-3.774-3.894-3.894C15.442 3.613 12 3.603 12 3.603zm0 4.268c-2.404 0-4.355 1.95-4.355 4.355s1.95 4.355 4.355 4.355 4.355-1.95 4.355-4.355S14.404 7.87 12 7.87zm0 7.27c-1.608 0-2.915-1.307-2.915-2.915S10.392 9.32 12 9.32s2.915 1.307 2.915 2.915-1.307 2.915-2.915 2.915zm4.805-7.87c-.665 0-1.203.538-1.203 1.203s.538 1.203 1.203 1.203 1.203-.538 1.203-1.203-.538-1.203-1.203-1.203z"></path>
                                </svg>
                            </a>
                            
                            <a 
                                href="https://x.com/aircoredynamics" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="p-2 rounded-lg bg-light-bg dark:bg-dark-bg hover:bg-light-primary dark:hover:bg-dark-primary hover:text-white transition-all group"
                                aria-label="X (Twitter)"
                            >
                                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
                                </svg>
                            </a>
                            
                            <a 
                                href="https://linkedin.com/company/aircoredynamics" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="p-2 rounded-lg bg-light-bg dark:bg-dark-bg hover:bg-light-primary dark:hover:bg-dark-primary hover:text-white transition-all group"
                                aria-label="LinkedIn"
                            >
                                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-12 pt-8 border-t border-light-border dark:border-dark-border">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="text-center md:text-left">
                            <p className="text-sm text-light-text/60 dark:text-dark-text/60 mb-2">
                                &copy; {currentYear} AircoreDynamics. All rights reserved.
                            </p>
                            <p className="text-xs text-light-text/50 dark:text-dark-text/50">
                                Course materials listed by{' '}
                                <a 
                                    href="https://www.merlot.org/merlot/viewMaterial.htm?id=773476432" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="hover:text-light-primary dark:hover:text-dark-primary transition-colors underline"
                                >
                                    MERLOT
                                </a>
                                {' '}(Multimedia Educational Resource for Learning and Online Teaching)
                            </p>
                        </div>
                        <p className="text-sm text-light-text/60 dark:text-dark-text/60 text-center md:text-right">
                            Made with HypLabs!
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;